const firebase = require("@/firebaseConfig.js");

const Client = require("@/models/Client.js");
const Skill = require("@/models/Skill.js");
const Preference = require("@/models/Preference.js");
const Project = require("@/models/Project.js");
const Provider = require("@/models/Provider.js");
const Goal = require("@/models/Goal.js");

const PROJECT_STATUS = [
  "applied",
  "accepted",
  "approved",
  "hidden",
  "rejected",
  "invited"
];

const Formatter = {
  methods: {
    /*
    Take in a value of {min:Number, max:Number}
    and output the average
    Eg: "124"
    */
    asAverage(value) {
      if (value && value.min && value.max) {
        return Math.floor((value.max + value.min) / 2);
      } else {
        return null;
      }
    },
    /*
    Take in a value of Number and
    output with commas and currency sign
    Eg: "$24,999"
    */
    asCurrency(value) {
      let currency = value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      return "$" + currency;
    },
    /*
    Take in a value of Number OR {min:Number, max:Number}
    and output as currency per hour
    Eg: "$125/hr"
    */
    asHourlyRate(value) {
      let rate = value;
      if (rate.min && rate.max) {
        rate = this.asAverage(rate);
      }
      rate = this.asCurrency(rate);
      return rate + "/hr";
    },
    /*
    Take in a value of String OR {first:String, last:String}
    and output as one string
    Eg: "Tim Cook"
    */
    asFullName(value) {
      let fullName = value;
      if (fullName.first && fullName.last) {
        fullName = fullName.first + " " + fullName.last;
      }
      return fullName;
    },
    /*
    Take in a value of String OR {first:String, last:String}
    and output as 'Undisclosed' every time
    */
    asAnon() {
      let anon = 'Undisclosed'
      return anon;
    },
    /*
    Take in a value of String OR {first:String, last:String}
    and output as one string
    Eg: "Tim Cook"
    */
    asShortDate(value) {
      if (value) {
        return value.toDate().toDateString();
      }
      return null;
    }
  }
};

const Skills = {
  methods: {
    /*
    Take in an Object, (either project or provider)
    and output the array of all attributes
    */
    allSkills(source) {
      return []
        .concat(source.preferences)
        .concat(source.skills)
        .concat(source.goals)
        .filter(a => a != undefined);
    },
    /*
    Take in a project:Object and a provider:Object
    and output the array of common skills
    */
    relevantSkills(project, provider) {
      let matches = [];
      let arr1 = this.allSkills(project);
      let arr2 = this.allSkills(provider);
      for (var a of arr1) {
        let match = arr2.find(b => a.id == b.id);
        if (match) {
          matches.push(match);
        }
      }
      return matches;
    },
    /*
    Take in a project:Object and a provider:Object
    and output the array of not common skills
    */
    irrelevantSkills(project, provider) {
      let all = this.allSkills(project);
      let common = this.relevantSkills(project, provider);
      return all.filter(a => !common.some(b => a.id == b.id));
    }
  }
};

const Status = {
  methods: {
    /*
    Take in a project:Object and a provider:Object
    and output the current status of that pair.
    If there are no explicit relationships, return false
    */
    currentStatus(project, provider) {
      let providerRef = provider.ref.id;
      let projectStatus = project.providers;
      if (!projectStatus) {
        return false;
      }
      for (let s of PROJECT_STATUS) {
        if (projectStatus[s].find(p => p && p.id == providerRef)) {
          return s;
        }
      }
      return false;
    },
    /*
    Take in a project:Object and a provider:Object
    and output the short current status of that pair.
    If there are no explicit relationships, return false
    */
    shortStatus(project, provider) {
      let s = this.currentStatus(project, provider);
      return s ? s.toUpperCase() : null;
    },
    /*
    Take in a project:Object and a provider:Object and a status:String
    and change the relationships in the project
    */
    changeStatus(project, provider, status) {
      let updateObject = {};
      for (let s of PROJECT_STATUS) {
        if (s == status) {
          updateObject[
            `providers.${s}`
          ] = firebase.firestore.FieldValue.arrayUnion(provider.ref);
        } else {
          updateObject[
            `providers.${s}`
          ] = firebase.firestore.FieldValue.arrayRemove(provider.ref);
        }
      }
      project.ref.update(updateObject);
      this.$forceUpdate();
    },
    /*
    Take in a project:Object and a provider:Object
    and output the current status of that pair.
    as seen from the provider's pov
    */
    statusAsProvider(project, provider) {
      let s = this.currentStatus(project, provider);
      switch (s) {
        case "applied":
          return "You have applied to this project";
        case "approved":
          return "You have been approved for this project";
        case "rejected":
          return "You have been rejected for this project";
        case "invited":
          return "You have been invited to interview";
        case "hidden":
          return "You have ignored this project";
        case "accepted":
          return "You have accepted this project";
        default:
          return "—";
      }
    },
    /*
    Take in a project:Object and a provider:Object
    and output the current status of that pair.
    as seen from the client's pov
    */
    statusAsClient(project, provider) {
      let s = this.currentStatus(project, provider);
      switch (s) {
        case "applied":
          return "They have applied to this project";
        case "approved":
          return "They have been approved for this project";
        case "rejected":
          return "They have been rejected for this project";
        case "invited":
          return "They have been invited to interview";
        case "hidden":
          return "They have ignored this project";
        case "accepted":
          return "They have accepted this project";
        default:
          return "—";
      }
    }
  }
};

const Actions = {
  methods: {
    model(section) {
      switch (section) {
        case "providers":
          return Provider.model;
        case "clients":
          return Client.model;
        case "projects":
          return Project.model;
        case "skills":
          return Skill.model;
        case "preferences":
          return Preference.model;
        case "goals":
          return Goal.model;
        default:
          return false;
      }
    },
    /*
    Take in email, password and data and create an Account, and then a User entry
    */
    createAccount(email, password, data) {
      return new Promise((resolve, reject) => {
        firebase.firebase
          .auth()
          .createUserWithEmailAndPassword(email, password)
          .then(result => {
            let x = this.createUser(result.user.uid, data);
            resolve(x);
          })
          .catch(error => {
            reject(error);
          });
      });
    },
    /*
    Take in a uid from an Account, and data and create a User
    */
    createUser(uid, data) {
      return new Promise((resolve, reject) => {
        var model = {};
        if (data.type == "client") {
          model = JSON.parse(JSON.stringify(Client.model));
        } else {
          model = JSON.parse(JSON.stringify(Provider.model));
        }
        data["created"] = firebase.firestore.FieldValue.serverTimestamp();
        let obj = Object.assign({}, model, data);
        let ref = firebase.db.collection("users").doc(uid);
        ref
          .set(obj)
          .then(() => {
            resolve(ref);
          })
          .catch(error => {
            reject(error);
          });
      });
    },
    /*
    Take in a uid from an Account, and data and create a User
    */
    createProject(data) {
      return new Promise((resolve, reject) => {
        let model = JSON.parse(JSON.stringify(Project.model));
        data["created"] = firebase.firestore.FieldValue.serverTimestamp();
        if (this.$root.user) {
          data["client"] = firebase.db
            .collection("users")
            .doc(this.$root.user.uid);
        }
        let obj = Object.assign({}, model, data);
        firebase.db
          .collection("projects")
          .add(obj)
          .then(() => {
            resolve(true);
          })
          .catch(error => {
            reject(error);
          });
      });
    },
    /*
    Take in a uid from an Account, and data and create a User
    */
    createOther(data, section) {
      return new Promise((resolve, reject) => {
        let _model = this.model(section);
        let model = JSON.parse(JSON.stringify(_model));
        data["created"] = firebase.firestore.FieldValue.serverTimestamp();
        let obj = Object.assign({}, model, data);
        firebase.db
          .collection(section.db)
          .add(obj)
          .then(ref => {
            resolve(ref);
          })
          .catch(error => {
            reject(error);
          });
      });
    }
  }
};

export {Formatter, Skills, Status, Actions};
